<script setup lang="ts">
import { computed } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'

type TStudentQuestionCardInfo = {
    serial: string
    subject: string
    prompt: string
    unread: boolean
    answeredCorrectly: boolean
    unanswered: boolean
    scenarioPartLabel?: string
}

const { Icon } = UIKit

const props = withDefaults(
    defineProps<{
        question: TStudentQuestionCardInfo
        numOfFlaggedQuestions?: number
        page: string
    }>(),
    {
        numOfFlaggedQuestions: 1,
    }
)

const emit = defineEmits<{
    click: []
    keypress: [keyEvent: KeyboardEvent]
}>()

const brandColors = BrandColors

const questionPrompt = computed(() => {
    return props.question.scenarioPartLabel
        ? `${props.question.scenarioPartLabel}: ${props.question.prompt}`
        : props.question.prompt
})

const emitClick = () => {
    emit('click')
}

const emitKeypress = (keyEvent: KeyboardEvent) => {
    emit('keypress', keyEvent)
}
</script>

<template>
    <div
        class="student-question-card"
        tabindex="0"
        role="button"
        :aria-label="`${
            props.question.answeredCorrectly ? 'Correct ' : 'Incorrect '
        }${ props.question.subject } ${props.question.prompt}`"
        @click="emitClick"
        @keypress.enter="emitKeypress($event)"
    >
        <div class="student-question-card__subject-icon-row">
            <div
                class="student-question-card__subject"
                :class="{'student-question-card__subject--content-page': props.page === 'content'}"
                aria-hidden
            >
                {{ props.question.subject }}
            </div>
            <div class="student-question-card__icons" aria-hidden>
                <div class="student-question-card__icon">
                    <Icon
                        type="flagFeedback"
                        :secondary-color="brandColors.bananaBread"
                        class="student-question-card__flag"
                        title="Flagged"
                    />
                </div>
                <div
                    v-if="!props.question.unanswered && props.question.answeredCorrectly"
                    class="student-question-card__icon"
                >
                    <Icon
                        type="correct"
                        class="student-question-card__correct"
                        title="Correct"
                    />
                </div>
                <div
                    v-if="!props.question.unanswered && !props.question.answeredCorrectly"
                    class="student-question-card__icon"
                >
                    <Icon
                        type="incorrect"
                        class="student-question-card__incorrect"
                        title="Incorrect"
                    />
                </div>
            </div>
        </div>
        <div
            class="student-question-card__prompt"
            :class="{
                'student-question-card__prompt--one-question': props.numOfFlaggedQuestions === 1,
                'student-question-card__prompt--content-page': props.page === 'content'
            }"
            v-html="questionPrompt"
        />
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';

.student-question-card {
    padding: 12px 12px 12px 12px;
    position: relative;
    background: $white;
    border-bottom: 1px solid $fog;
    user-select: none;
    cursor: pointer;
    outline: none;

    &:last-child {
        margin-bottom: 16px;
    }

    &:hover {
        background-color: color.mix($barely-background, $brand-black, 96%);
    }

    &:focus {
        &::after {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            border: 1px solid $brand-blue;
            border-radius: 6px;
        }
    }

    &__subject-icon-row {
        display: flex;
        justify-content: space-between;
    }

    &__subject {
        margin-bottom: 6px;
        font-size: 14px;
        line-height: 20px;
        color: $slate;
        font-weight: 500;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;

        &--content-page {
            line-height: 18px;
        }
    }

    &__prompt {
        color: $brand-black;
        font-size: 15px;
        line-height: 22px;
        width: 307px;
        margin-top: auto;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;

        :deep(p) {
            margin: 0;
        }

        &--one-question {
            color: $brand-black;
            font-size: 15px;
            width: 307px;
            line-height: 22px;
            margin-top: auto;
            padding-left: 2px;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 10;
            overflow: hidden;
        }
    }

    &__icons {
        margin-top: -2px;
        display: flex;
        align-items: center;
    }

    &__icon {
        text-align: center;
    }

    &__flag {
        color: #ce8500;
        height: 19px;
    }

    &__correct {
        color: $cadaverous;
        height: 19px;
    }

    &__incorrect {
        color: $pepper;
        height: 19px;
    }
}
</style>
